import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Bot, ArrowRight, DollarSign, ShieldCheck, Search } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export type CatalogueAgent = {
  id: string;
  name: string;
  tagline: string;
  description: string;
  price: number;
  imageUrl?: string | null;
};

const ACCENT_COLORS = [
  { glow: "rgba(0,229,255,0.18)", icon: "#22d3ee", btn: "#22d3ee", btnHover: "#67e8f9" },
  { glow: "rgba(192,38,211,0.18)", icon: "#e879f9", btn: "#c026d3", btnHover: "#d946ef" },
  { glow: "rgba(139,92,246,0.18)", icon: "#a78bfa", btn: "#8b5cf6", btnHover: "#a78bfa" },
  { glow: "rgba(236,72,153,0.18)", icon: "#f472b6", btn: "#ec4899", btnHover: "#f472b6" },
  { glow: "rgba(0,229,255,0.18)", icon: "#22d3ee", btn: "#22d3ee", btnHover: "#67e8f9" },
  { glow: "rgba(192,38,211,0.18)", icon: "#e879f9", btn: "#c026d3", btnHover: "#d946ef" },
  { glow: "rgba(139,92,246,0.18)", icon: "#a78bfa", btn: "#8b5cf6", btnHover: "#a78bfa" },
  { glow: "rgba(236,72,153,0.18)", icon: "#f472b6", btn: "#ec4899", btnHover: "#f472b6" },
  { glow: "rgba(0,229,255,0.18)", icon: "#22d3ee", btn: "#22d3ee", btnHover: "#67e8f9" },
  { glow: "rgba(192,38,211,0.18)", icon: "#e879f9", btn: "#c026d3", btnHover: "#d946ef" },
];

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onBuildAgent: (agent: CatalogueAgent) => void;
  agents: CatalogueAgent[];
  loaded?: boolean;
};

export function CatalogueModal({ isOpen, onClose, onBuildAgent, agents, loaded = true }: Props) {
  const [query, setQuery] = useState("");

  const filtered = query.trim()
    ? agents.filter((a) => {
        const q = query.toLowerCase();
        return (
          a.name.toLowerCase().includes(q) ||
          a.tagline.toLowerCase().includes(q) ||
          a.description.toLowerCase().includes(q)
        );
      })
    : agents;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="catalogue-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
          style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(8px)" }}
          onClick={onClose}
        >
          <motion.div
            key="catalogue-panel"
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="relative flex flex-col w-full max-w-4xl rounded-2xl overflow-hidden scifi-wave-bg border border-cyan-400/25"
            style={{
              boxShadow: "0 24px 80px rgba(0,0,0,0.55), 0 0 40px rgba(0,229,255,0.15)",
              height: "80vh",
              maxHeight: "80vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center gap-4 px-6 py-5 shrink-0 border-b border-cyan-400/15 bg-black/40 backdrop-blur-sm">
              {/* Icon + title */}
              <div className="flex items-center gap-3 shrink-0">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center border border-cyan-400/40"
                  style={{
                    background: "linear-gradient(135deg, rgba(192,38,211,0.4) 0%, rgba(0,229,255,0.4) 100%)",
                    boxShadow: "0 0 16px rgba(0,229,255,0.35)",
                  }}
                >
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2
                    className="text-lg font-bold text-white leading-none"
                    style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
                  >
                    Find the perfect AI agent for you!
                  </h2>
                  <p className="text-xs text-slate-300 mt-0.5 flex items-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5 text-cyan-300 animate-pulse shrink-0" />
                    Fixed price · 100% money-back guarantee
                  </p>
                </div>
              </div>

              {/* Search bar */}
              <div
                className="flex flex-1 items-center gap-2 px-3 py-2 rounded-xl"
                style={{
                  background: "rgba(0,0,0,0.45)",
                  border: "1px solid rgba(0,229,255,0.2)",
                  boxShadow: "0 0 12px rgba(0,229,255,0.06)",
                }}
              >
                <Search className="w-4 h-4 text-cyan-400/70 shrink-0" />
                <input
                  type="text"
                  aria-label="Search agents catalogue"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, role, or description…"
                  className="flex-1 bg-transparent text-sm text-white placeholder-slate-500 outline-none"
                  style={{ fontFamily: "'Spline Sans', sans-serif" }}
                />
                {query && (
                  <button
                    onClick={() => setQuery("")}
                    className="p-0.5 rounded-full text-slate-400 hover:text-cyan-300 hover:bg-cyan-400/10 transition-colors shrink-0"
                    title="Clear search"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Close */}
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-cyan-400/10 transition-colors text-slate-400 hover:text-cyan-300 shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Agent grid */}
            <div className="overflow-y-auto flex-1 px-6 py-5 bg-black/30" style={{ minHeight: "420px" }}>
              {!loaded ? (
                <div className="text-center text-slate-400 py-12">Loading agents…</div>
              ) : filtered.length === 0 ? (
                <div className="text-center text-slate-400 py-12">
                  {query ? `No agents matching "${query}".` : "No agents available."}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {filtered.map((agent, i) => {
                    const color = ACCENT_COLORS[i % ACCENT_COLORS.length];
                    return (
                      <motion.div
                        key={agent.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.04, duration: 0.2 }}
                        className="flex flex-col rounded-xl p-4 gap-3 bg-black/50 backdrop-blur-sm border border-cyan-400/20 hover:border-cyan-400/40 transition-colors"
                        style={{
                          boxShadow: `0 0 24px ${color.glow}`,
                        }}
                      >
                        {/* Agent header row */}
                        <div className="flex items-start gap-3">
                          <div
                            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5 overflow-hidden border"
                            style={{
                              background: `${color.icon}1a`,
                              borderColor: `${color.icon}55`,
                            }}
                          >
                            {agent.imageUrl ? (
                              <img
                                src={`${BASE}/api/storage${agent.imageUrl}`}
                                alt={agent.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Bot className="w-5 h-5" style={{ color: color.icon }} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <h3
                                className="font-bold text-white text-[15px] leading-tight"
                                style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
                              >
                                {agent.name}
                              </h3>
                              <span className="text-xs text-slate-400 font-medium leading-tight">
                                {agent.tagline}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 mt-1">
                              <DollarSign className="w-3 h-3 text-cyan-300" />
                              <span className="text-sm font-semibold text-cyan-200">
                                {agent.price.toLocaleString()}
                              </span>
                              <span className="text-xs text-slate-500 ml-0.5">fixed price</span>
                            </div>
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-sm text-slate-300 leading-relaxed">
                          {agent.description}
                        </p>

                        {/* CTA */}
                        <button
                          onClick={() => { onBuildAgent(agent); onClose(); }}
                          className="mt-auto flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-slate-950 text-sm font-bold transition-all active:scale-95"
                          style={{
                            background: color.btn,
                            boxShadow: `0 0 18px ${color.glow}`,
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = color.btnHover)}
                          onMouseLeave={(e) => (e.currentTarget.style.background = color.btn)}
                        >
                          Build this agent
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 shrink-0 flex items-center justify-between border-t border-cyan-400/15 bg-black/40 backdrop-blur-sm">
              <p className="text-xs text-slate-400">
                Don't see what you need?{" "}
                <button
                  onClick={onClose}
                  className="text-cyan-300 hover:text-cyan-200 font-medium transition-colors"
                >
                  Chat with Bluna
                </button>{" "}
                — we build anything.
              </p>
              <p className="text-xs text-slate-400">All agents include a 100% money-back guarantee.</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
